import { ChevronDown, ChevronRight } from 'lucide-react';

import { useTheme } from '../../hooks/useTheme';

import type { NavRailItem, NavSection } from './config';
import { NAV_THEMES } from './nav-tokens';
import NavItemLink from './NavItemLink';
import type { NavItemVariant } from './navRowClass';
import NavSectionDot from './NavSectionDot';
import { SECTION_ROW } from './navSectionRowClass';
import { sectionBadge } from './section-badge';

interface NavSectionDisclosureProps {
  section: NavSection;
  items: NavRailItem[];
  expanded: boolean;
  onToggle: () => void;
  /** Row shape for the sub-items — the drawer's, or the labelled panel's. */
  itemVariant: NavItemVariant;
  /** Called before navigating away, so the drawer can dismiss. */
  onNavigate?: () => void;
}

/** One section row in the mobile drawer, plus its pages when expanded. Two
 *  shapes: inert for an unbuilt section, and a disclosure otherwise. The nav
 *  model drops ready sections with no pages for this viewer, so the row never
 *  has to stand in for its children. */
export default function NavSectionDisclosure({
  section,
  items,
  expanded,
  onToggle,
  itemVariant,
  onNavigate,
}: NavSectionDisclosureProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const Icon = section.icon;
  const badge = sectionBadge(section.label, items);

  if (!section.ready) {
    return (
      <div className={SECTION_ROW} style={{ color: t.faint }}>
        <Icon size={18} aria-hidden className="shrink-0" />
        <span>{section.label}</span>
        <span
          className="ml-auto rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide"
          style={{ background: t.surface, color: t.faint }}
        >
          Soon
        </span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={badge.has ? badge.name : undefined}
        title={badge.has ? badge.name : undefined}
        className={`${SECTION_ROW} cursor-pointer`}
        style={{ color: expanded ? t.moss : t.ink }}
      >
        <Icon size={18} aria-hidden className="shrink-0" />
        <span>{section.label}</span>
        {badge.has && <NavSectionDot />}
        <span className="ml-auto" aria-hidden>
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </span>
      </button>
      {expanded && (
        <div
          className="ml-5 flex flex-col gap-0.5 pl-2"
          style={{ borderLeft: `1px solid ${t.border}` }}
        >
          {items.map((item) => (
            <NavItemLink key={item.id} item={item} variant={itemVariant} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </>
  );
}
